import React, { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { creditsApi } from '../utils/api';
import { useAnalytics } from '../hooks/useAnalytics';

// BILL-CRED: issue an account credit to a single client. Credits live at the
// client level (ledger, computed balance) and are applied later at settlement
// via the payment dialog's "Apply Available Credit" option. A credit tied to
// a co-owned horse does NOT auto-split — staff decide who receives it.
const CREDIT_SOURCES: Array<{ value: 'overpayment' | 'void_in_lieu' | 'goodwill' | 'prepayment'; label: string }> = [
  { value: 'goodwill', label: 'Goodwill / comp' },
  { value: 'overpayment', label: 'Overpayment' },
  { value: 'prepayment', label: 'Prepayment' },
  { value: 'void_in_lieu', label: 'Credit in lieu of void' },
];

interface IssueCreditDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  clientId: string;
  clientName?: string;
  // Prefills for the invoice-detail "Issue Credit" path (void-ineligible invoice)
  defaultAmount?: number;
  defaultSource?: 'overpayment' | 'void_in_lieu' | 'goodwill' | 'prepayment';
  linkedInvoiceId?: string;
  linkedInvoiceNumber?: string;
}

const IssueCreditDialog: FC<IssueCreditDialogProps> = ({
  open,
  onClose,
  onSuccess,
  clientId,
  clientName,
  defaultAmount,
  defaultSource,
  linkedInvoiceId,
  linkedInvoiceNumber,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { trackFeatureUsed } = useAnalytics();
  const queryClient = useQueryClient();

  const [amount, setAmount] = useState<string>('');
  const [source, setSource] = useState<string>('goodwill');
  const [reason, setReason] = useState<string>('');
  // Idempotency key: one uuid per dialog-open, RESEEDED whenever a submission-shaping value
  // changes (see the effect below). A double-click or network retry re-sends the SAME key and
  // the server returns the original entry — but an EDITED resubmit after a timeout must NOT
  // carry the old key, or the server (correctly) 409s it as a key reuse with different
  // parameters. Reseeding on edit means: same values -> replay-safe, changed values -> new key.
  const [requestId, setRequestId] = useState<string>('');

  // Re-seed the form each time the dialog opens (prefills can differ per invoke)
  useEffect(() => {
    if (open) {
      setAmount(defaultAmount != null && defaultAmount > 0 ? defaultAmount.toFixed(2) : '');
      setSource(defaultSource || 'goodwill');
      setReason('');
      setRequestId(
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      );
    }
  }, [open, defaultAmount, defaultSource]);

  // Reseed the key when the values that shape the entry change — the half of the idempotency
  // contract the server cannot enforce (credit-integrity F2: before this, a timeout-after-commit
  // followed by an edited resubmit silently re-issued the OLD amount).
  useEffect(() => {
    if (!open) return;
    setRequestId(
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    );
  }, [amount, source, reason]);

  const amountNum = Number(amount);
  const amountValid = Number.isFinite(amountNum) && amountNum > 0;

  const issueMutation = useMutation(
    () =>
      creditsApi.issue({
        clientId,
        amount: Math.round(amountNum * 100) / 100,
        source: source as any,
        reason: reason.trim() || undefined,
        linkedInvoiceId,
        requestId: requestId || undefined,
      }),
    {
      onSuccess: (res) => {
        trackFeatureUsed('credit_issued');
        queryClient.invalidateQueries(['client-credits', clientId]);
        // Interpolate the SERVER's entry, never local state — printing the local amount was half
        // the F2 bug: a replayed old-amount issue used to show the user the NEW number.
        enqueueSnackbar(
          `Credit of $${((res.entry?.amountCents ?? Math.round(amountNum * 100)) / 100).toFixed(2)} issued — available balance $${res.balance.toFixed(2)}`,
          { variant: 'success' },
        );
        onSuccess?.();
        onClose();
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to issue credit', { variant: 'error' });
      },
    },
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Issue Account Credit</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={0.5}>
          {clientName && (
            <Typography variant="body2" color="text.secondary">
              Credit will be added to <strong>{clientName}</strong>'s account and can be
              applied to their invoices at payment time.
            </Typography>
          )}
          {linkedInvoiceNumber && (
            <Typography variant="body2" color="text.secondary">
              Linked to invoice <strong>#{linkedInvoiceNumber}</strong>.
            </Typography>
          )}
          <TextField
            autoFocus
            label="Amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            inputProps={{ min: 0.01, step: 0.01 }}
            error={amount !== '' && !amountValid}
            helperText={amount !== '' && !amountValid ? 'Enter a positive amount' : undefined}
            fullWidth
          />
          <TextField
            select
            label="Source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            fullWidth
          >
            {CREDIT_SOURCES.map((s) => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            inputProps={{ maxLength: 500 }}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => issueMutation.mutate()}
          disabled={!amountValid || issueMutation.isLoading}
          startIcon={issueMutation.isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {issueMutation.isLoading ? 'Issuing…' : 'Issue Credit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IssueCreditDialog;
